/**
 * @file useCategoryTree.ts
 * @description Данные и действия страницы «Категории» (phase14.0.2;
 * phase19.31 — образец (б) разбора B): загрузка, раскрытие веток, публикация
 * словами через меню строки, удаление с подтверждением окном кабинета,
 * перестановка перетаскиванием внутри одного родителя.
 *
 * После любой правки дерево перечитывается (счётчики товаров и вид от
 * родителя считает сервер). Перестановка показывается сразу; отказ сервера
 * возвращает прежний порядок. Перенос в другую ветку перетаскиванием не
 * делается — человеку так и говорим, а не молчим.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { useNotify } from "../../ui/NotifyProvider";
import { useConfirm } from "../../ui/use-confirm";
import { catalogCategories, type CatalogCategory } from "../../../lib/api-catalog";
import { ApiError } from "../../../lib/api-client";
import { buildCategoryTree, categoryLabel } from "../../../lib/catalog-category-tree";
import { CATEGORY_PUBLISH_VALUE, type CategoryPublishMode } from "../../../lib/catalog-status";

/** Текст отказа: перетащили к категории другого родителя. */
export const REORDER_OTHER_PARENT_TEXT =
  "Переставлять можно только среди соседей — внутри одной ветки. В другую ветку категорию перетаскиванием не перенести";

export type ReorderResult =
  | { kind: "ok"; items: Array<{ id: string; sortOrder: number }> }
  /** Другой родитель — человеку объясняем, что так нельзя. */
  | { kind: "otherParent" }
  /** Ничего не меняется (то же место, системный корень, нет такой строки). */
  | { kind: "none" };

/**
 * Новый порядок соседей после перетаскивания (`sortOrder` шагом 10).
 * Системные корни не двигаются и в запрос не попадают: их порядок задаёт
 * платформа.
 */
export function reorderSiblings(categories: CatalogCategory[], activeId: string, overId: string): ReorderResult {
  const active = categories.find((c) => c.id === activeId);
  const over = categories.find((c) => c.id === overId);
  if (!active || !over || active.isSystemRoot || over.isSystemRoot || activeId === overId) return { kind: "none" };
  if ((active.parentId ?? null) !== (over.parentId ?? null)) return { kind: "otherParent" };
  const siblings = categories
    .filter((c) => (c.parentId ?? null) === (active.parentId ?? null) && !c.isSystemRoot)
    .sort((a, b) => a.sortOrder - b.sortOrder || categoryLabel(a).localeCompare(categoryLabel(b), "ru"));
  const from = siblings.findIndex((c) => c.id === activeId);
  const to = siblings.findIndex((c) => c.id === overId);
  if (from === -1 || to === -1) return { kind: "none" };
  const next = [...siblings];
  const [moved] = next.splice(from, 1);
  if (!moved) return { kind: "none" };
  next.splice(to, 0, moved);
  return { kind: "ok", items: next.map((c, idx) => ({ id: c.id, sortOrder: idx * 10 })) };
}

/** Применить новый порядок к списку (показываем сразу, до ответа сервера). */
export function applySortOrders(
  categories: CatalogCategory[],
  items: Array<{ id: string; sortOrder: number }>,
): CatalogCategory[] {
  const order = new Map(items.map((i) => [i.id, i.sortOrder]));
  return categories.map((c) => (order.has(c.id) ? { ...c, sortOrder: order.get(c.id) ?? c.sortOrder } : c));
}

export function useCategoryTree() {
  const notify = useNotify();
  const confirmApi = useConfirm();
  const { confirm } = confirmApi;
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await catalogCategories.list();
      setCategories(data);
      // Первая загрузка — системные корни раскрыты.
      setExpanded((prev) => (prev.size > 0 ? prev : new Set(data.filter((c) => c.isSystemRoot).map((c) => c.id))));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить категории. Проверьте связь и попробуйте ещё раз");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const tree = useMemo(() => buildCategoryTree(categories), [categories]);
  const byId = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /** Выполнить правку, сообщить и перечитать дерево. */
  const mutate = useCallback(
    async (work: () => Promise<unknown>, done: string | null, failed: string) => {
      try {
        await work();
        if (done) notify.success(done);
        await refresh();
      } catch (err) {
        notify.error(err instanceof ApiError ? err.message : failed);
      }
    },
    [notify, refresh],
  );

  const setPublish = useCallback(
    (c: CatalogCategory, mode: CategoryPublishMode) =>
      void mutate(
        () => catalogCategories.update(c.id, { isPublishedOnSite: CATEGORY_PUBLISH_VALUE[mode] }),
        "Публикация категории изменена",
        "Не удалось изменить публикацию",
      ),
    [mutate],
  );

  const remove = useCallback(
    async (c: CatalogCategory) => {
      const ok = await confirm({
        title: `Удалить категорию «${categoryLabel(c)}»?`,
        description: "Категорию с товарами или подкатегориями удалить нельзя — сначала перенесите их.",
        action: "Удалить",
        danger: true,
      });
      if (ok) await mutate(() => catalogCategories.remove(c.id), "Категория удалена", "Не удалось удалить категорию");
    },
    [confirm, mutate],
  );

  const reorder = useCallback(
    (activeId: string, overId: string) => {
      const result = reorderSiblings(categories, activeId, overId);
      if (result.kind === "otherParent") {
        notify.info(REORDER_OTHER_PARENT_TEXT);
        return;
      }
      if (result.kind !== "ok") return;
      // Новый порядок виден сразу; сервер отказал — возвращаем прежний и
      // перечитываем дерево, чтобы на экране был порядок сервера.
      const before = categories;
      setCategories(applySortOrders(categories, result.items));
      void (async () => {
        try {
          await catalogCategories.reorder(result.items);
          await refresh();
        } catch (err) {
          setCategories(before);
          notify.error(err instanceof ApiError ? err.message : "Не удалось сохранить порядок. Прежний порядок восстановлен");
          await refresh();
        }
      })();
    },
    [categories, notify, refresh],
  );

  return {
    categories,
    tree,
    byId,
    loading,
    error,
    refresh,
    expanded,
    toggle,
    setPublish,
    remove,
    reorder,
    confirmRequest: confirmApi.request,
    settleConfirm: confirmApi.settle,
  };
}

export type CategoryTreeState = ReturnType<typeof useCategoryTree>;
